import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PartAttribute, PartAttributeSearchParams } from '../../services/partAttributesApi';

export interface PartAttributesState {
  items: PartAttribute[];
  selectedItem: PartAttribute | null;
  loading: boolean;
  error: string | null;
  formData: {
    PartId: number | null;
    Name: string;
    Description: string;
    Active: boolean;
  };
  validationErrors: string[];
  searchParams: PartAttributeSearchParams;
  pagination: { page: number; pageSize: number; total: number };
  statistics: { total: number; active: number; inactive: number };
  selectedIds: number[];
  bulkOperationLoading: boolean;
  successMessage: string;
}

const initialState: PartAttributesState = {
  items: [],
  selectedItem: null,
  loading: false,
  error: null,
  formData: { PartId: null, Name: '', Description: '', Active: true },
  validationErrors: [],
  searchParams: { page: 1, pageSize: 10, search: '', active: undefined, sortBy: 'Name', sortOrder: 'asc' },
  pagination: { page: 1, pageSize: 10, total: 0 },
  statistics: { total: 0, active: 0, inactive: 0 },
  selectedIds: [],
  bulkOperationLoading: false,
  successMessage: '',
};

const partAttributesSlice = createSlice({
  name: 'partAttributes',
  initialState,
  reducers: {
    setLoading: (state, a: PayloadAction<boolean>) => { state.loading = a.payload; },
    setError: (state, a: PayloadAction<string | null>) => { state.error = a.payload; },
    setItems: (state, a: PayloadAction<PartAttribute[]>) => { state.items = a.payload; },
    setSelectedItem: (state, a: PayloadAction<PartAttribute | null>) => { state.selectedItem = a.payload; },
    addItem: (state, a: PayloadAction<PartAttribute>) => { state.items.unshift(a.payload); },
    updateItem: (state, a: PayloadAction<PartAttribute>) => {
      const i = state.items.findIndex(it => it.Id === a.payload.Id);
      if (i !== -1) state.items[i] = a.payload;
      if (state.selectedItem?.Id === a.payload.Id) state.selectedItem = a.payload;
    },
    removeItem: (state, a: PayloadAction<number>) => {
      state.items = state.items.filter(it => it.Id !== a.payload);
      if (state.selectedItem?.Id === a.payload) state.selectedItem = null;
    },
    setFormData: (state, a: PayloadAction<Partial<PartAttributesState['formData']>>) => {
      state.formData = { ...state.formData, ...a.payload };
    },
    resetFormData: (state) => { state.formData = initialState.formData; },
    setValidationErrors: (state, a: PayloadAction<string[]>) => { state.validationErrors = a.payload; },
    clearValidationErrors: (state) => { state.validationErrors = []; },
    setSearchParams: (state, a: PayloadAction<Partial<PartAttributeSearchParams>>) => {
      state.searchParams = { ...state.searchParams, ...a.payload } as PartAttributesState['searchParams'];
    },
    resetSearchParams: (state) => { state.searchParams = initialState.searchParams; },
    setPagination: (state, a: PayloadAction<Partial<PartAttributesState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...a.payload } as PartAttributesState['pagination'];
    },
    setStatistics: (state, a: PayloadAction<PartAttributesState['statistics']>) => { state.statistics = a.payload; },
    setSelectedIds: (state, a: PayloadAction<number[]>) => { state.selectedIds = a.payload; },
    addSelectedId: (state, a: PayloadAction<number>) => { if (!state.selectedIds.includes(a.payload)) state.selectedIds.push(a.payload); },
    removeSelectedId: (state, a: PayloadAction<number>) => { state.selectedIds = state.selectedIds.filter(id => id !== a.payload); },
    clearSelectedIds: (state) => { state.selectedIds = []; },
    setBulkOperationLoading: (state, a: PayloadAction<boolean>) => { state.bulkOperationLoading = a.payload; },
    setSuccessMessage: (state, a: PayloadAction<string>) => { state.successMessage = a.payload; },
    clearSuccessMessage: (state) => { state.successMessage = ''; },
    resetPartAttributesState: () => initialState,
  },
});

export const {
  setLoading, setError, setItems, setSelectedItem, addItem, updateItem, removeItem,
  setFormData, resetFormData, setValidationErrors, clearValidationErrors,
  setSearchParams, resetSearchParams, setPagination, setStatistics,
  setSelectedIds, addSelectedId, removeSelectedId, clearSelectedIds, setBulkOperationLoading,
  setSuccessMessage, clearSuccessMessage, resetPartAttributesState,
} = partAttributesSlice.actions;

export const FETCH_PART_ATTRIBUTES = 'partAttributes/fetch';
export const FETCH_PART_ATTRIBUTE_BY_ID = 'partAttributes/fetchById';
export const CREATE_PART_ATTRIBUTE = 'partAttributes/create';
export const UPDATE_PART_ATTRIBUTE = 'partAttributes/update';
export const DELETE_PART_ATTRIBUTE = 'partAttributes/delete';
export const SEARCH_PART_ATTRIBUTES = 'partAttributes/search';
export const FETCH_PART_ATTRIBUTES_STATS = 'partAttributes/fetchStats';
export const BULK_PART_ATTRIBUTES_ACTIVATE = 'partAttributes/bulkActivate';
export const BULK_PART_ATTRIBUTES_DEACTIVATE = 'partAttributes/bulkDeactivate';
export const BULK_PART_ATTRIBUTES_DELETE = 'partAttributes/bulkDelete';
export const EXPORT_PART_ATTRIBUTES_CSV = 'partAttributes/exportCsv';

export const fetchPartAttributes = (p?: PartAttributeSearchParams) => ({ type: FETCH_PART_ATTRIBUTES, payload: p });
export const fetchPartAttributeById = (id: number) => ({ type: FETCH_PART_ATTRIBUTE_BY_ID, payload: id });
export const createPartAttribute = (item: Omit<PartAttribute, 'Id'>) => ({ type: CREATE_PART_ATTRIBUTE, payload: item });
export const updatePartAttribute = (id: number, item: Partial<PartAttribute>) => ({ type: UPDATE_PART_ATTRIBUTE, payload: { id, item } });
export const deletePartAttribute = (id: number) => ({ type: DELETE_PART_ATTRIBUTE, payload: id });
export const searchPartAttributes = (p: PartAttributeSearchParams) => ({ type: SEARCH_PART_ATTRIBUTES, payload: p });
export const fetchPartAttributesStats = () => ({ type: FETCH_PART_ATTRIBUTES_STATS });
export const bulkPartAttributesActivate = (ids: number[]) => ({ type: BULK_PART_ATTRIBUTES_ACTIVATE, payload: ids });
export const bulkPartAttributesDeactivate = (ids: number[]) => ({ type: BULK_PART_ATTRIBUTES_DEACTIVATE, payload: ids });
export const bulkPartAttributesDelete = (ids: number[]) => ({ type: BULK_PART_ATTRIBUTES_DELETE, payload: ids });
export const exportPartAttributesCsv = (p?: PartAttributeSearchParams) => ({ type: EXPORT_PART_ATTRIBUTES_CSV, payload: p });

export default partAttributesSlice.reducer; 